import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import type { ApiResponse } from '@shared-models';
import { API_CONFIG, type ApiConfig } from '../api-config';
import { mapMutationMessage } from '../../utils/map-pageable';

/** Response de prueba de vida del customer. */
export interface CustomerLifeTestDto {
  id: string;
  customerId: string;
  selfieUrl: string;
  contentType?: string;
  fileSize?: number;
  source?: string;
  verificationStatus?: string;
  rejectionReason?: string | null;
  verifiedAt?: string | null;
  faceSimilarity?: number | null;
  faceMatchProvider?: string | null;
  comparedIdentificationId?: string | null;
  updatedAt?: string;
}

/**
 * Prueba de vida del cliente.
 *   GET    /api/customers/{customerId}/life-test
 *   PUT    /api/customers/{customerId}/life-test  (multipart)
 *   DELETE /api/customers/{customerId}/life-test
 *
 * Multipart parts aceptados: proofLife (preferido), selfie, file, proofLife.file.
 * JPEG/PNG ~10 KB–8 MB.
 * Roles: GET staff · PUT/DELETE ADMIN/MANAGER (backend).
 */
@Injectable({ providedIn: 'root' })
export class CustomerLifeTestService {
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private resource(customerId: string): string {
    return `${this.apiConfig.apiUrl}/customers/${customerId}/life-test`;
  }

  get(customerId: string): Observable<CustomerLifeTestDto | null> {
    return this.http
      .get<ApiResponse<CustomerLifeTestDto>>(this.resource(customerId))
      .pipe(
        map((res) => res.data ?? null),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404) return of(null);
          return throwError(() => err);
        }),
      );
  }

  upsert(customerId: string, file: File): Observable<CustomerLifeTestDto> {
    const formData = new FormData();
    formData.append('proofLife', file, file.name || 'proof-life.jpg');
    return this.http
      .put<ApiResponse<CustomerLifeTestDto>>(
        this.resource(customerId),
        formData,
      )
      .pipe(map((res) => res.data as CustomerLifeTestDto));
  }

  delete(customerId: string): Observable<{ message: string }> {
    return this.http
      .delete<{ success?: boolean; message?: string }>(
        this.resource(customerId),
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Prueba de vida eliminada exitosamente'),
        ),
      );
  }
}
